import React, { useState } from "react";
import {
  useCapitalCalls,
  useCreateCapitalCall,
  useUpdateCapitalCall,
  useDeleteCapitalCall,
} from "@/hooks/useCapitalCalls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { isApiError } from "@/api/client";
import { formatDate } from "@/lib/utils";
import type { CapitalCall } from "@/types";

const fmtBRL = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

// Ordinal em inglês: 1st, 2nd, 3rd, 4th… As exceções 11/12/13 (eleventh,
// twelfth, thirteenth) usam "th" apesar de terminarem em 1/2/3.
function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

interface Props {
  fundId: string;
  isAdmin: boolean;
}

export function CapitalCallsTab({ fundId, isAdmin }: Props) {
  const { data: calls, isLoading } = useCapitalCalls(fundId);
  const createCall = useCreateCapitalCall(fundId);
  const updateCall = useUpdateCapitalCall(fundId);
  const deleteCall = useDeleteCapitalCall(fundId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CapitalCall | null>(null);
  const [draftDate, setDraftDate] = useState("");
  // Percentual guardado em centavos (inteiro). Ex.: 1050 = 10,50%. Máx 10000 (100,00%).
  const [draftPctCents, setDraftPctCents] = useState(0);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Exibição estilo calculadora: 1050 -> "10,50"
  const pctDisplay = (cents: number) =>
    (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Captura dígito a dígito (direita -> esquerda) e Backspace; trava em 100,00%
  const handlePctKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key >= "0" && e.key <= "9") {
      e.preventDefault();
      const next = draftPctCents * 10 + Number(e.key);
      if (next <= 10000) setDraftPctCents(next);
    } else if (e.key === "Backspace") {
      e.preventDefault();
      setDraftPctCents(Math.floor(draftPctCents / 10));
    }
  };

  const sorted = [...(calls ?? [])].sort((a, b) => a.call_number - b.call_number);
  const totalPct = sorted.reduce((s, c) => s + Number(c.percentage), 0);
  const totalBrl = sorted.reduce((s, c) => s + Number(c.amount_brl ?? 0), 0);
  const remaining = 100 - totalPct;

  const openAdd = () => {
    setEditing(null);
    setDraftDate("");
    setDraftPctCents(0);
    setDialogOpen(true);
  };

  const openEdit = (c: CapitalCall) => {
    setEditing(c);
    setDraftDate(c.call_date ? c.call_date.slice(0, 10) : "");
    setDraftPctCents(Math.round(Number(c.percentage) * 100));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const pct = draftPctCents / 100;
    if (!draftDate) { toast.error("Enter the date"); return; }
    if (!pct || pct <= 0 || pct > 100) { toast.error("Invalid percentage (0–100)"); return; }
    try {
      if (editing) {
        await updateCall.mutateAsync({ id: editing.id, call_date: draftDate, percentage: pct });
        toast.success("Capital call updated");
      } else {
        await createCall.mutateAsync({ call_date: draftDate, percentage: pct });
        toast.success("Capital call created");
      }
      setDialogOpen(false);
    } catch (e) {
      const msg = isApiError(e) ? e.response?.data?.error ?? "Error saving" : "Error saving";
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteCall.mutateAsync(deleteId);
      toast.success("Capital call removed");
    } catch {
      toast.error("Error removing");
    }
    setDeleteId(null);
  };

  const thS: React.CSSProperties = {
    backgroundColor: "rgb(212, 163, 123)",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "11px",
    padding: "6px 8px",
    whiteSpace: "nowrap",
    textAlign: "center",
  };
  const tdS: React.CSSProperties = {
    fontSize: "12px",
    padding: "5px 8px",
    whiteSpace: "nowrap",
    borderBottom: "1px solid hsl(var(--border))",
  };
  const tdCS: React.CSSProperties = { ...tdS, textAlign: "center" };
  const tdRS: React.CSSProperties = { ...tdS, textAlign: "right" };
  const totS: React.CSSProperties = {
    backgroundColor: "rgb(0, 56, 130)",
    color: "#e2eaf5",
    fontWeight: 700,
    fontSize: "12px",
    padding: "6px 8px",
    whiteSpace: "nowrap",
  };
  const totRS: React.CSSProperties = { ...totS, textAlign: "right" };

  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Allocated: {totalPct.toFixed(2)}% · Remaining: {remaining.toFixed(2)}%
          </span>
          <Button size="sm" onClick={openAdd} disabled={remaining <= 0} className="gap-2">
            <Plus className="h-4 w-4" /> New Capital Call
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-border overflow-x-auto">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...thS, textAlign: "center" }}>Call</th>
              <th style={{ ...thS, textAlign: "center" }}>Date</th>
              <th style={{ ...thS, textAlign: "right" }}>Percentage</th>
              <th style={{ ...thS, textAlign: "right" }}>Amount (BRL)</th>
              {isAdmin && <th style={{ ...thS, width: "64px" }} />}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td style={tdCS} colSpan={isAdmin ? 5 : 4}>Loading…</td></tr>
            )}
            {!isLoading && sorted.length === 0 && (
              <tr><td style={{ ...tdCS, color: "hsl(var(--muted-foreground))" }} colSpan={isAdmin ? 5 : 4}>
                No capital calls recorded.
              </td></tr>
            )}
            {sorted.map((c, idx) => (
              <tr key={c.id} style={{ backgroundColor: idx % 2 === 0 ? "hsl(var(--card))" : "rgb(242, 242, 242)" }}>
                <td style={{ ...tdCS, fontWeight: 600 }}>{ordinal(c.call_number)}</td>
                <td style={tdCS}>{formatDate(c.call_date)}</td>
                <td style={tdRS}>{Number(c.percentage).toFixed(2)}%</td>
                <td style={tdRS}>{fmtBRL(Number(c.amount_brl ?? 0))}</td>
                {isAdmin && (
                  <td style={{ ...tdS, padding: "2px 4px" }}>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteId(c.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {sorted.length > 0 && (
              <tr>
                <td style={{ ...totS, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }} colSpan={2}>
                  Total ({sorted.length})
                </td>
                <td style={totRS}>{totalPct.toFixed(2)}%</td>
                <td style={totRS}>{fmtBRL(totalBrl)}</td>
                {isAdmin && <td style={totS} />}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${ordinal(editing.call_number)} capital call` : "New Capital Call"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Date</span>
              <Input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} className="h-9" />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Percentage (%)</span>
              <Input
                type="text"
                inputMode="numeric"
                value={pctDisplay(draftPctCents)}
                onKeyDown={handlePctKeyDown}
                onChange={() => { /* edição controlada por onKeyDown */ }}
                className="h-9 text-right"
              />
              {!editing && (
                <span className="text-[11px] text-muted-foreground">
                  Remaining available: {remaining.toFixed(2)}%
                </span>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createCall.isPending || updateCall.isPending}>
              {editing ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove capital call?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The numbering of the other calls will not change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
